import { safeStorage, type SafeStorage } from './safe-storage'

const RELOAD_MARKER_KEY = 'app:chunk-reload-at'
/** A second failure within this window means the reload did not help — stop reloading. */
export const CHUNK_RELOAD_WINDOW_MS = 60_000

const CHUNK_ERROR_PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
  /Unable to preload CSS/i,
  /Loading (CSS )?chunk [\w-]+ failed/i,
]

export function isChunkLoadError(error: unknown): error is Error {
  if (!(error instanceof Error)) return false
  if (error.name === 'ChunkLoadError') return true
  return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(error.message))
}

export interface ChunkRecoveryEnv {
  storage: SafeStorage
  reload: () => void
  now: () => number
}

const defaultEnv = (): ChunkRecoveryEnv => ({
  storage: safeStorage('session'),
  reload: () => {
    window.location.reload()
  },
  now: () => Date.now(),
})

/**
 * Reload once to pick up the current deployment. Returns `exhausted` when a reload already
 * happened recently, so callers show a manual reload action instead of looping.
 */
export function attemptChunkRecovery(
  env: ChunkRecoveryEnv = defaultEnv(),
): 'reloading' | 'exhausted' {
  const last = Number(env.storage.get(RELOAD_MARKER_KEY) ?? '0')
  const now = env.now()
  if (Number.isFinite(last) && last > 0 && now - last < CHUNK_RELOAD_WINDOW_MS) {
    return 'exhausted'
  }
  env.storage.set(RELOAD_MARKER_KEY, String(now))
  env.reload()
  return 'reloading'
}

/**
 * Handles Vite's `vite:preloadError` (stale lazy chunk after a redeploy). When recovery is
 * exhausted the error propagates to the route error boundary, which offers a manual reload.
 */
export function installChunkErrorHandler(
  target: Pick<Window, 'addEventListener'> = window,
  env?: ChunkRecoveryEnv,
): void {
  target.addEventListener('vite:preloadError', (event) => {
    if (attemptChunkRecovery(env) === 'reloading') event.preventDefault()
  })
}
